<script>
function prefixOf(name) {
    if (!name) {
        return 'other';
    }
    if (name.startsWith('beestore.soap.')) {
        return 'beestore.soap';
    }
    const dot = name.indexOf('.');
    return dot === -1 ? name : name.slice(0, dot);
}

const PREFIX_COLORS = {
    http: '#3c8dbc',
    job: '#00a65a',
    pipe: '#605ca8',
    'beestore.soap': '#f39c12',
    other: '#777',
};

export default {
    name: 'SpanWaterfall',
    props: {
        entries: { type: Array, default: () => [] },
    },
    computed: {
        spans() {
            return this.entries
                .filter((entry) => entry.message === 'span.completed' && entry.duration_ms != null)
                .map((entry) => {
                    const start = Date.parse(entry.ts_utc);
                    return {
                        id: entry.id,
                        spanId: entry.span_id || null,
                        parentSpanId: entry.parent_span_id || null,
                        depth: 0,
                        name: entry.span_name || (entry.context && entry.context.name) || entry.message,
                        requestId: entry.request_id || '—',
                        start,
                        duration: Number(entry.duration_ms),
                        status: entry.span_status || (entry.context && entry.context.status) || 'ok',
                        prefix: prefixOf(entry.span_name || (entry.context && entry.context.name)),
                    };
                })
                .filter((span) => Number.isFinite(span.start))
                .sort((a, b) => a.start - b.start);
        },
        window() {
            if (!this.spans.length) {
                return { min: 0, max: 1, range: 1 };
            }
            const min = Math.min(...this.spans.map((s) => s.start));
            const max = Math.max(...this.spans.map((s) => s.start + s.duration));
            return { min, max, range: Math.max(max - min, 1) };
        },
        lanes() {
            const byRequest = {};
            this.spans.forEach((span) => {
                if (!byRequest[span.requestId]) {
                    byRequest[span.requestId] = [];
                }
                byRequest[span.requestId].push(span);
            });
            return Object.entries(byRequest).map(([requestId, spans]) => ({
                requestId,
                spans: this.withDepth(spans),
            }));
        },
    },
    methods: {
        withDepth(spans) {
            const byId = new Map();
            spans.forEach((span) => {
                if (span.spanId) {
                    byId.set(span.spanId, span);
                }
            });
            return spans.map((span) => {
                let depth = 0;
                let parent = span.parentSpanId;
                const seen = new Set();
                while (parent && byId.has(parent) && !seen.has(parent) && depth < 8) {
                    seen.add(parent);
                    depth += 1;
                    parent = byId.get(parent).parentSpanId;
                }
                return { ...span, depth };
            });
        },
        barStyle(span) {
            const left = ((span.start - this.window.min) / this.window.range) * 100;
            const width = Math.max((span.duration / this.window.range) * 100, 0.4);
            return {
                left: `${left}%`,
                width: `${width}%`,
                background: PREFIX_COLORS[span.prefix] || PREFIX_COLORS.other,
                opacity: span.status === 'error' ? 0.55 : 1,
            };
        },
    },
};
</script>

<template>
    <div class="span-waterfall">
        <p v-if="!spans.length" class="text-muted">No span.completed rows in this correlation.</p>
        <template v-else>
            <div v-for="lane in lanes" :key="lane.requestId" class="span-waterfall-lane">
                <div class="span-waterfall-lane-label">
                    <code>{{ lane.requestId }}</code>
                </div>
                <div class="span-waterfall-track">
                    <div
                        v-for="span in lane.spans"
                        :key="span.id"
                        class="span-waterfall-bar"
                        :style="barStyle(span)"
                        :title="`${span.name} · ${span.duration} ms · ${span.status}`"
                    >
                        <span class="span-waterfall-bar-label" :style="{ paddingLeft: (span.depth * 10) + 'px' }">{{ span.name }} {{ span.duration }}ms</span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
.span-waterfall-lane {
    display: flex;
    align-items: stretch;
    margin-bottom: 8px;
    min-height: 28px;
}
.span-waterfall-lane-label {
    width: 120px;
    flex-shrink: 0;
    font-size: 11px;
    padding-right: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
}
.span-waterfall-track {
    position: relative;
    flex: 1;
    background: #f4f4f4;
    min-height: 28px;
}
.span-waterfall-bar {
    position: absolute;
    top: 4px;
    height: 20px;
    border-radius: 2px;
    color: #fff;
    font-size: 10px;
    line-height: 20px;
    overflow: hidden;
    white-space: nowrap;
    padding: 0 4px;
}
.span-waterfall-bar-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
